import { Component, OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Track } from '@app/core/models/Track';
import { LiveTrackingState } from '@app/core/states/live-tracking.state';
import { criteria } from '@app/library/models/criterias.model';
import { PlaylistApi } from '@app/library/models/playlist-api.model';
import { Playlist } from '@app/library/models/playlist.model';
import {
  CreatePlaylistDynamicRequest,
  CreatePlaylistManualFailure,
  CreatePlaylistManualRequest,
  CreatePlaylistManualSuccess,
  InvalidCriteria,
} from '@app/library/states/playlists.actions';
import { TracksState } from '@app/library/states/tracks.state';
import { emptyCriterionValidator } from '@app/shared/custom-validators';
import { isEqualFormData, mSanitize } from '@app/shared/utils';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Store } from '@ngxs/store';
import {
  ErrorFormFormat,
  FormHelperService,
  UiStoreHelperService,
} from '@radioking/shared/common-services';
import { CloseCurrentPanel, DynamicEnabledPanel } from '@radioking/shared/pannel-manager';
import { SugestionInput } from '@radioking/ui/input';
import { InputColorPickerComponent } from '@radioking/ui/input-color-picker';
import amplitude from 'amplitude-js';
import { cloneDeep } from 'lodash';
import { Observable, Subject, Subscription } from 'rxjs';

export function getCriteriaTitle(field: string, translate: TranslateService): string {
  switch (field) {
    case criteria.BOX:
      return translate.instant(marker('PLAYLIST.CRITERIA.BOX'));
    case criteria.ARTIST:
      return translate.instant(marker('PLAYLIST.CRITERIA.ARTIST'));
    case criteria.TITLE:
      return translate.instant(marker('PLAYLIST.CRITERIA.TITLE'));
    case criteria.ALBUM:
      return translate.instant(marker('PLAYLIST.CRITERIA.ALBUM'));
    case criteria.YEAR:
      return translate.instant(marker('PLAYLIST.CRITERIA.YEAR'));
    case criteria.BPM:
      return translate.instant(marker('PLAYLIST.CRITERIA.BPM'));
    case criteria.PLAYTIME:
      return translate.instant(marker('PLAYLIST.CRITERIA.DURATION_FORMAT'));
    case criteria.UPLOAD_DATE:
      return translate.instant(marker('PLAYLIST.CRITERIA.UPLOAD_DATE'));
    case criteria.TAGS:
      return translate.instant(marker('PLAYLIST.CRITERIA.TAG'));
  }

  return '-';
}

export function getCriteriaType(field: string): string {
  switch (field) {
    case 'artist':
    case 'title':
    case 'album':
      return 'text';
    case 'idtrackbox':
      return 'box';
    case 'year':
    case 'bpm':
    case 'playtime_seconds':
      return 'number';
    case 'upload_date':
      return 'date';
    case 'tags':
      return 'tags';
  }

  return 'text';
}

const sortString = (a: string, b: string) => {
  const aLower = a.toLowerCase();
  const bLower = b.toLowerCase();

  return aLower.localeCompare(bLower);
};

export function getCriteriaTextSuggestion(
  field: criteria,
  store: Store,
): SugestionInput[] {
  const withChronics =
    ['Discover', 'Demo'].indexOf(store.selectSnapshot(LiveTrackingState.planDesc)) < 0;
  const trackData: Track[] = store.selectSnapshot(
    withChronics
      ? TracksState.allTracksForDropdown
      : TracksState.allTracksButChronicsForDropdown,
  );
  const inputData = trackData
    .map(t =>
      field === criteria.TITLE ? t.title : field === criteria.ARTIST ? t.artist : t.album,
    )
    .filter(t => !!t)
    .sort(sortString);

  // return store.selectSnapshot(TracksState.allAlbums)(withChronics);
  return inputData
    .filter((item, index) => inputData.indexOf(item) === index)
    .map((a, i) => ({ id: i, name: a }));
}

@Component({
  selector: 'rk-library-create-playlist',
  templateUrl: './library-create-playlist.component.html',
  styleUrls: ['./library-create-playlist.component.scss'],
})
export class LibraryCreatePlaylistComponent implements OnInit, DynamicEnabledPanel {
  createPlaylistForm: UntypedFormGroup;
  createPlaylistFormErrors: ErrorFormFormat;
  initialFormData: Partial<Playlist> = {};

  isSaving$: Observable<boolean>;

  submitSubject: Subject<void> = new Subject<void>();

  subscription = new Subscription();

  constructor(
    private readonly formHelper: FormHelperService,
    private readonly fb: UntypedFormBuilder,
    private readonly helper: UiStoreHelperService,
    private readonly translateService: TranslateService,
    private readonly store: Store,
  ) {}

  ngOnInit() {
    amplitude.getInstance().logEvent('add playlist form');
    this.createForm();

    this.isSaving$ = this.helper.getLoadingState(
      [CreatePlaylistManualRequest],
      [CreatePlaylistManualSuccess, CreatePlaylistManualFailure],
    );
  }

  hasToShowConfirmOnExit(): boolean {
    return false;
  }

  isDirty(): boolean {
    return (
      this.createPlaylistForm &&
      !this.createPlaylistForm.pristine &&
      !isEqualFormData(this.createPlaylistForm.getRawValue(), this.initialFormData)
    );
  }

  get criteria() {
    return this.createPlaylistForm.get('criteria') as UntypedFormArray;
  }

  createForm() {
    this.createPlaylistFormErrors = {
      name: {},
      color: {},
      type: {},
    };
    this.createPlaylistForm = this.fb.group({
      name: ['', Validators.required],
      color: [InputColorPickerComponent.getRandomColor(), Validators.required],
      type: 'manual',
      criteria: this.fb.array([], emptyCriterionValidator()),
    });

    this.initialFormData = cloneDeep({ ...this.createPlaylistForm.getRawValue() });

    this.subscription.add(
      this.createPlaylistForm.valueChanges.subscribe(() => {
        this.createPlaylistForm.clearValidators();
        this.formHelper.updateErrorMapping(
          this.createPlaylistForm,
          this.createPlaylistFormErrors,
        );
      }),
    );
    this.formHelper.updateErrorMapping(
      this.createPlaylistForm,
      this.createPlaylistFormErrors,
    );
  }

  addCriteria(field: string) {
    this.criteria.push(this.fb.control({ field, operator: 'equals', value: '' }));
    setTimeout(() => {
      const sideNav: HTMLElement = document.querySelector(
        'rk-sidenav-action-wrapper .content',
      );
      sideNav.style.scrollBehavior = 'smooth';
      sideNav.scrollTop = 9999;
      sideNav.style.scrollBehavior = 'auto';
    }, 200);
  }

  removeCriteria(index: number) {
    this.criteria.removeAt(index);
  }

  getCriteriaType(field: string): string {
    return getCriteriaType(field);
  }

  getCriteriaTitle(field: string): string {
    return getCriteriaTitle(field, this.translateService);
  }

  getCriteriaTextSuggestion(field: criteria): SugestionInput[] {
    return getCriteriaTextSuggestion(field, this.store);
  }

  @Dispatch()
  exitPanel = () => new CloseCurrentPanel(true);

  @Dispatch()
  invalidCriteria = () => new InvalidCriteria();

  submit() {
    if (this.createPlaylistForm.get('type').value === 'dynamic') {
      this.submitSubject.next();
      if (this.createPlaylistForm.invalid) {
        this.invalidCriteria();

        return;
      }
      this.createDynamicPlaylist();

      return;
    }
    this.createManualPlaylist();
  }

  @Dispatch()
  createManualPlaylist = () => {
    const formValues = this.createPlaylistForm.value;
    const playlist: Partial<PlaylistApi> = {
      name: mSanitize(formValues.name),
      color: formValues.color,
      type: formValues.type,
    };

    return new CreatePlaylistManualRequest(playlist);
  };

  @Dispatch()
  createDynamicPlaylist = () => {
    const formValues = this.createPlaylistForm.value;
    const playlist: Partial<PlaylistApi> = {
      name: mSanitize(formValues.name),
      color: formValues.color,
      type: formValues.type,
      criteria: formValues.criteria,
    };

    return new CreatePlaylistDynamicRequest(playlist);
  };

  getCriteriaLength(): number {
    return (this.createPlaylistForm.get('criteria') as UntypedFormArray).length;
  }
}
